<script>
import {
  defineComponent,
  inject,
  computed,
  watchEffect,
  ref,
  watch,
  nextTick,
} from "vue";
import NavMainSearch from "@kit/components/NavMainSearch.vue";
import { searchWidgetMain, navbar } from "@project/themes";
import NavBarBase from "@kit/components/NavBarBase.vue";
import IconButton from "@kit/components/IconButton.vue";
import Icon from "@kit/components/Icon.vue";
import Column from "@kit/components/Column.vue";
import { inBrowser } from "@kit/utils/EnvironmentHelper";
import { cmsasset } from "@kit/utils/Formats";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import "@css/index.css";

export default defineComponent({
  name: "NavBar",
  setup() {
    const hydrate = inject("hydrate");
    const store = useStore();
    const navbarTheme = navbar;
    const searchWidgetMainTheme = searchWidgetMain;
    const route = useRoute();
    const googleTranslateElement = ref(null);
    const atTop = ref(true);

    const { prefetch } = hydrate({
      prefetch: ["navAndFooter", "routes"],
    });

    const okToShow = computed(() => {
      return route.name != "contact-us";
    });

    const inMobile = computed(() => {
      return store.state.inMobile;
    });

    watchEffect(() => {
      if (googleTranslateElement.value) {
        if (inBrowser) {
          if (window.MAKE_TRANSLATION_INSTANCE) {
            console.log(
              "Notice: 49348 firing contingency for the google translate element"
            );
            window.MAKE_TRANSLATION_INSTANCE();
          }
        }
      }
    });

    //inspect with /gtfs-routes?pretty
    //return the routes and also add in some colors.
    const serviceAgencies = computed(() => {
      const navAndFooter =
        prefetch.value && prefetch.value.navAndFooter
          ? prefetch.value.navAndFooter
          : null;

      const gtfsRoutes = prefetch.value?.routes;

      if (navAndFooter && gtfsRoutes) {
        const agencyArr = navAndFooter.routes_and_services;

        const colors = {};
        gtfsRoutes.forEach((route) => {
          colors[route.route_id] = route;
        });

        // We're going to skip this for aria.
        for (let i = 0; i < agencyArr.length; i++) {
          const agency = agencyArr[i];
          const { use_route_color_id, color, text_color } = agency;

          let bColor = "#FFFFFF";
          let tColor = "#333333";

          //If we're supposed to use one of the route colors for the service-agency colors,
          //then use it.
          if (use_route_color_id) {
            const r = colors[use_route_color_id];
            if (r) {
              const { aria_color, aria_text_color } = r;
              bColor = aria_color;
              tColor = aria_text_color;
            }
          }

          //else, just use the override color from the agency
          else {
            bColor = color;
            tColor = text_color;
          }

          agency["final_text_color"] = tColor;
          agency["final_color"] = bColor;
        }

        return agencyArr;
      }

      return [];
    });

    //
    // const handleNavMouseOver = (e) => {
    //   const el = e.target.parentNode;
    //   el.style.backgroundColor = el.getAttribute("data-hoverColor");
    // };
    // const handleNavMouseOut = (e) => {
    //   const el = e.target.parentNode;
    //   el.style.backgroundColor = el.getAttribute("data-regularColor");
    // };

    watch(
      inMobile,
      async (newValue, _oldValue) => {
        if (inBrowser) {
          await nextTick();

          const dContainer = document.getElementById(
            "translate-widget-container-desktop"
          );
          const mContainer = document.getElementById("translate-widget-container-mobile");
          const widget = document.getElementById("google_translate_element");

          if (newValue) {
            if (widget.parentNode == dContainer) {
              dContainer.removeChild(widget);
              mContainer.appendChild(widget);
            }
          } else {
            if (widget.parentNode == mContainer) {
              mContainer.removeChild(widget);
              dContainer.appendChild(widget);
            }
          }
        }
      },
      { immediate: true }
    );

    const topOn = () => {
      atTop.value = true;
    };
    const topOff = () => {
      atTop.value = false;
    };

    return {
      atTop,
      searchWidgetMainTheme,
      navbarTheme,
      serviceAgencies,
      //handleNavMouseOver,
      //handleNavMouseOut,
      cmsasset,
      okToShow,
      googleTranslateElement,
      topOn,
      topOff,
    };
  },
  components: {
    NavMainSearch,
    NavBarBase,
    IconButton,
    Icon,
    Column,
  },
});
</script>

<template>
  <NavBarBase @TopOn="topOn" @TopOff="topOff" :hideNav="!okToShow" :theme="navbarTheme">
    <template v-slot:top-area>
      <Column
        tag="div"
        ltP="100"
        ltT="100"
        ltD="100"
        gtB="1400"
        gtH="1400"
        class="sb-h sb-content-space-between sb-align-2bottom sb-alt2"
      >
        <router-link
          to="/"
          class="sb sb-text hta-desktop-logo-container"
          style="z-index: 3; padding-left: 20px"
        >
          <img
            alt="Humboldt Transit Authority big white logo in the navbar"
            style="object-fit: contain; width: auto; height: 100%"
            :src="cmsasset('/cms-static-assets/HTA_NavLogo.png')"
          />
        </router-link>
        <div v-show="atTop" class="sb sb-h sb-gr sb-g10">
          <NavMainSearch :theme="searchWidgetMainTheme" />

          <div
            ref="googleTranslateElement"
            class="sb sb-h"
            id="translate-widget-container-desktop"
          >
            <Icon
              class="sb sb-text"
              width="4rem"
              :from-project="false"
              height="4em"
              style="fill: #ffffff"
              icon="translation"
            />
            <div
              class="sb sb-text sb-greedy google-translate-container"
              id="google_translate_element"
            ></div>
          </div>
        </div>
      </Column>
    </template>

    <template
      v-slot:nav-area="{ navItemClasses, dropDownNavItemClasses, navSubItemClasses }"
    >
      <Column
        tag="div"
        ltP="100"
        ltT="100"
        ltD="100"
        gtB="1400"
        gtH="1400"
        class="sb-h sb-align-2right"
      >
        <div :class="dropDownNavItemClasses">
          Fares
          <div
            :class="`${navSubItemClasses} hta-top-nav-routes-subitems`"
            aria-description="This is a menu showing navigation links for fares and passes"
            role="menu"
            aria-orientation="vertical"
          >
            <router-link
              class="sb hta-navbar-dropdown-option"
              to="/fares/purchase-tickets-online/"
            >
              Purchase
            </router-link>

            <router-link class="sb hta-navbar-dropdown-option" to="/fares"
              >Fares &amp; Passes
            </router-link>
          </div>
        </div>

        <router-link to="/how-to-ride" :class="navItemClasses"> How to Ride </router-link>

        <div :class="dropDownNavItemClasses">
          Routes
          <div
            :class="`${navSubItemClasses} hta-top-nav-routes-subitems`"
            aria-description="This is a menu showing the available services from HTA."
            role="menu"
            aria-orientation="vertical"
          >
            <template v-if="serviceAgencies.length > 0">
              <router-link
                v-for="serviceAgency in serviceAgencies"
                class="sb hta-navbar-dropdown-option"
                :to="
                  serviceAgency.url
                    ? serviceAgency.url
                    : `/agencies/${serviceAgency.url_slug}`
                "
                :style="`color:${serviceAgency.final_text_color}; background-color:${serviceAgency.final_color};`"
              >
                {{ serviceAgency.display_name }}
              </router-link>
            </template>
          </div>
        </div>
        <router-link to="/contact-us" :class="navItemClasses">Contact </router-link>
        <router-link to="/news" :class="navItemClasses">News</router-link>
      </Column>
    </template>
  </NavBarBase>
  <!-- <div v-else></div> -->
</template>
